import { useEffect, useState } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

import icClose from "@/assets/images/user/icClose.svg";
import icZoomIn from "@/assets/images/user/icZoomIn.svg";
import icZoomOut from "@/assets/images/user/icZoomOut.svg";
import { Dialog, DialogClose, DialogContent, DialogTrigger } from "@/components/ui/dialog";

// fit: "contain" = 화면 안에 전부 들어오게(기본) / "width" = 가로 100% 로 채우고 세로는 넘치면 팬
export default function ImgZoomPop({
    img,
    zoomImg,
    alt = "이미지",
    fit = "contain",
}: {
    img: string;
    /**
     * 확대 팝업 전용 고해상도(2배수) 소스. 없으면 img 를 그대로 쓴다.
     * 팝업은 뷰포트 폭까지 이미지를 늘려 그리기 때문에, 페이지용 1.5배수 파일로는
     * DPR 2 화면에서 필요한 물리 픽셀의 절반밖에 안 된다.
     * Dialog 는 닫혀 있을 때 내용을 언마운트하므로 이 파일은 팝업을 열 때만 로드된다.
     */
    zoomImg?: string;
    alt?: string;
    fit?: "contain" | "width";
}) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [maxScale, setMaxScale] = useState<number>(4);

    useEffect(() => {
        const updateMaxScale = () => {
            setMaxScale(window.innerWidth >= 768 ? 4 : 8);
        };

        const handleResize = () => {
            if (isOpen) {
                setIsOpen(false);
            }
            updateMaxScale();
        };

        updateMaxScale();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [isOpen]);

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild className="cursor-pointer">
                <img src={img} alt={alt} />
            </DialogTrigger>
            <DialogContent className="flex h-[100dvh] max-w-none items-center justify-center gap-0 overflow-hidden !rounded-none border-none bg-transparent p-0 shadow-none [&>button]:hidden">
                <TransformWrapper initialScale={1} minScale={1} maxScale={maxScale} centerOnInit>
                    {({ zoomIn, zoomOut }) => (
                        <>
                            {/* 뷰포트를 꽉 채운 뒤 fit 규칙대로 이미지를 가운데 정렬.
                                width 모드는 content 높이를 고정하면 안 된다.
                                라이브러리가 content 크기로 팬 가능 범위를 계산하기 때문에,
                                !h-full 을 주면 이미지가 화면보다 길어도 범위가 0 이라 드래그가 되돌아온다. */}
                            <TransformComponent
                                wrapperClass="!h-full !w-full"
                                contentClass={
                                    fit === "width" ? "!w-full" : "!h-full !w-full items-center justify-center"
                                }
                            >
                                <img
                                    src={zoomImg ?? img}
                                    alt={alt}
                                    className={
                                        fit === "width"
                                            ? "w-full shrink-0 object-contain"
                                            : "max-h-full max-w-full object-contain"
                                    }
                                />
                            </TransformComponent>
                            <div className="fixed bottom-[20px] left-1/2 z-50 flex -translate-x-1/2 items-center justify-center gap-[24px] rounded-[40px] bg-[rgba(0,0,0,0.6)] p-[8px_20px]">
                                <button
                                    type="button"
                                    onClick={() => zoomIn()}
                                    className="transition-opacity hover:opacity-80"
                                >
                                    <img src={icZoomIn} alt="확대" className="size-[24px] md:size-[40px]" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => zoomOut()}
                                    className="transition-opacity hover:opacity-80"
                                >
                                    <img src={icZoomOut} alt="축소" className="size-[24px] md:size-[40px]" />
                                </button>
                                <DialogClose asChild>
                                    <button type="button" className="transition-opacity hover:opacity-80">
                                        <img src={icClose} alt="닫기" className="size-[24px] md:size-[40px]" />
                                    </button>
                                </DialogClose>
                            </div>
                        </>
                    )}
                </TransformWrapper>
            </DialogContent>
        </Dialog>
    );
}
